import Enumerable from "linq";
import { IDocumentEditable } from "..";
import { IItem, instanceOfIDrawableItem, IDrawableItem, TextBoxItem, IExpandable, instanceOfIExpandable, RichTextBoxItem, LineItem } from "../../../../domain/document/items";
import { Section } from "../../../../domain/document";
import { Point, Rectangle } from "../../../../domain/drawing";
import { TimeMeasure } from "../../../../utilities";
import { IConfiguration } from "../../../../domain/configuration";
import store from "../../../../store";

export class ObjectPositionSlider {
  constructor(private _source: IDocumentEditable) {
  }

  public apply(itemSetId: number, oldItem: IItem, updatedItem: IItem): void {
    if (instanceOfIDrawableItem(updatedItem)
     && instanceOfIDrawableItem(oldItem)
     && updatedItem.isRelayoutableByHeight
     && (oldItem.geometry.rectangle.height !== updatedItem.geometry.rectangle.height)) {
      TimeMeasure.log("オブジェクト高さ変化による座標系調整", () => {
        const y = oldItem.geometry.rectangle.bottomLeft.y;
        const offset = updatedItem.geometry.rectangle.height - oldItem.geometry.rectangle.height;
        const sectionIndex = this.getSectionIndexFromPrivateId(itemSetId, updatedItem.privateId);
        const mergedOffset = this.getMergedOffset(sectionIndex, y, offset, updatedItem.privateId);
        this.relayoutItemsOnHeightUpdated(sectionIndex, y, mergedOffset, oldItem.geometry.rectangle);
      });
    }
  }

  private getSectionIndexFromPrivateId(itemSetId: number, privateId: number): number {
    for (let index = 0; index < this._source.sections.length; ++index) {
      const section = this._source.sections[index];
      if (section.itemSetId ===  itemSetId && this.hasItem(section, privateId)) { return index; }
    }
    return -1;
  }

  private hasItem(section: Section, privateId: number): boolean {
    return !!section.itemPrivateIds.find(id => id === privateId);
  }

  /**
   * 横に並んだ可変オブジェクトのどちらの値を採用するかどうか判定して、移動させる値を算出する
   * 現状では複数のオブジェクトが複雑なレイアウトで横に並んでいるときには対応できない
   */
  private getMergedOffset(sectionIndex: number, y: number, offset: number, excludePrivateId: number): number {
    if (!offset) { return 0; }
    const upper = Math.min(y, y + offset);
    const lower = Math.max(y, y + offset);
    const frontLine = this.getExtendedFrontLine(sectionIndex, upper, excludePrivateId);
    if (!frontLine) { return offset; }
    if (frontLine > lower) { return 0; }
    return offset > 0
      ? y + offset - frontLine
      : frontLine - y;
  }

  /**
   * 指定の位置より下で、拡張されている前線の最も上にあるものを取得する
   */
  private getExtendedFrontLine(sectionIndex: number, y: number, excludePrivateId: number): number {
    return Enumerable.from(this.getDrawableItems(sectionIndex))
      .where(i => y > i.geometry.rectangle.y && i.privateId !== excludePrivateId)
      .aggregate(null, (frontLine, item) => this.getMoreFrontLine(frontLine, item, y));
  }

  private getMoreFrontLine(value: number, item: IDrawableItem, y: number): number {
    if (this.isHeightAdjustedItem(item)) {
      const itemFrontLine = item.geometry.rectangle.y + item.geometry.rectangle.height;
      if ((y < itemFrontLine) && (!value || (itemFrontLine < value))) {
        return itemFrontLine;
      }
    }
    return value;
  }

  private isHeightAdjustedItem(item: IDrawableItem): boolean {
    // TODO: refactor (IHeightAutoAdjustableがTextBoxItemのほうしか判定できなくて中途半端になっている)
    return ((item instanceof TextBoxItem) || (item instanceof RichTextBoxItem)) && (item.originalHeight !== item.geometry.rectangle.height);
  }

  private getDrawableItems(sectionIndex: number): IDrawableItem[] {
    if (sectionIndex < 0) { throw new Error("sectionIndex is invalid!!!"); }
    const section = this._source.sections[sectionIndex];
    const itemSet = this._source.itemSets[section.itemSetId];
    return Enumerable.from(section.itemPrivateIds)
      .select(i => itemSet.items[i])
      .where(i => instanceOfIDrawableItem(i) && !this.isUnhandledObject(section, i))
      .select(i => i as IDrawableItem)
      .toArray();
  }

  /**
   * オブジェクトがページの外側にあると、フォーマットエディタ上で見えないためメンテナンスできない
   * ページの外側のオブジェクトは文書編集中に表示されず・印刷にも描画されない
   * いくつかのフォーマットではコンテンツチームも把握していないオブジェクトが領域外にある模様・・
   */
  private isUnhandledObject(section: Section, item: IDrawableItem): boolean {
    return !section.isPageSplittable
        && (item.geometry.rectangle.y > section.size.height);
  }

  private relayoutItemsOnHeightUpdated(sectionIndex: number, y: number, offset: number, sourceRect: Rectangle): void {
    if (sectionIndex < 0) { throw new Error("sectionIndex is invalid!!!"); }
    const itemSetId = this._source.sections[sectionIndex].itemSetId;
    Enumerable.from(this.getDrawableItems(sectionIndex))
      .forEach(i => {
        if (this.isExpandTargetItem(y, i, sourceRect)) {
          // 枠線を伸ばす処理
          this.updateItem(i as IDrawableItem & IExpandable, itemSetId, s => s.expandToBottom(offset));
        } else if (this.isMoveTargetItem(y, i)) {
          // 位置をずらす処理
          // ※枠線を伸ばす処理と位置をずらす処理は同時には行わない
          this.updateItem(i, itemSetId, (s) => s.move(new Point(0, offset)));
        }
      });
  }

  private updateItem<T extends IItem>(source: T, itemSetId: number, write: (s: T) => void): void {
    const cloned = source.clone() as T;
    write(cloned);
    this._source.updateItem(cloned, {
      itemSetId,
      isGeometryOnly: true,
    });
  }

  private isExpandTargetItem(y: number, item: IDrawableItem, sourceRect: Rectangle): boolean {
    const configuration = store.getters.configuration as IConfiguration;
    if (configuration.behavior.document.editor.objects.shouldTransformSideLocatedObjectsByRelayout) {
      // 横並びの線・矩形も可変対象とする昔の挙動
      // 2.109.0.0までのバージョンで稼働した一部の案件ではこの挙動になっているため、メジャーバージョンアップ時の機能踏襲のため判定を残している
      return (y > item.geometry.rectangle.y)
        && (y <= (item.geometry.rectangle.y + item.geometry.rectangle.height))
        && instanceOfIExpandable(item);
    } else {
      // 実装当初は横並びの線も変形の対象としていたが、V1と挙動を合わせて可変するオブジェクトを囲う矩形のみを変形の対象としている
      return (y > item.geometry.rectangle.y)
        && (y <= (item.geometry.rectangle.y + item.geometry.rectangle.height))
        && (sourceRect.topLeft.y >= item.geometry.rectangle.topLeft.y)
        && (sourceRect.topLeft.x >= item.geometry.rectangle.topLeft.x)
        && (sourceRect.topRight.x <= item.geometry.rectangle.topRight.x)
        && instanceOfIExpandable(item)
        && !(item instanceof LineItem);
    }
  }

  private isMoveTargetItem(y: number, item: IDrawableItem): boolean {
    return (y <= item.geometry.rectangle.y) && !item.isPositionFixed;
  }
}
